import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Company } from '../Model/Company';
import { Accountant } from '../Model/Accountant';
import { Client } from '../Model/Client';
import { clientApi } from './client';
import { BankAccount } from '../Model/BankAccount';
import { aggreg8Api } from './aggreg8';
import { transactionApi } from './transaction';
import { ExtendedTransaction } from '../Model/Transaction';
import { fileApi } from './file';
import { bankAccountApi } from './bankaccount';
import { RootState } from './store';
import { readFromSessionStorage } from './util';
import { Aggreg8Account } from '../Model/Aggreg8Account';
import { companyApi } from './company';
import { Aggreg8Bank } from '../Model/Aggreg8Bank';
import { OptionFromCompany } from '../Components/AccountToCommpany/useCompanyCreatableDropdown';
import { BankConfig } from '../Model/BankConfig';
import { bankConfigApi } from './bankConfig';
import { UserFlowInitResponse } from '../Model/UserFlow';
import { TransactionFilter } from '../Model/Others';

export type ParamsBagStore = {
  currentCompany: Partial<Company>;
  currentAccountant: Partial<Accountant>;
  secretBag: Partial<Client>;
  currentClient: Partial<Client>;
  currentBankAccount: Partial<BankAccount>;
  selectedBankAccounts: Pick<BankAccount, 'accountNr' | 'originate'>[];
  transactions: Partial<BankAccount>[];
  userFlowInitResponse: UserFlowInitResponse;
  accessToken: string;
  bankAccountList: BankAccount[];
  transactionList: ExtendedTransaction[];
  prevTransactions: ExtendedTransaction[];
  currentFileId: string;
  longPolling: string[];
  taxNumber: string;
  aggreg8AccountList?: Aggreg8Account[];
  companyList: Company[];
  aggreg8BankList: Aggreg8Bank[];
  accountToCompanyListIsLoaded: boolean;
  options?: OptionFromCompany[];
  accountListLength: number;
  bankConfigList: BankConfig[];
  transactionViewerFilter: TransactionFilter;
  skippedFilter: { id: string; value: unknown }[];
  isRlb: boolean;
  clientList?: Client[];
};

export const paramsBagSlice = createSlice({
  name: 'paramsBag',
  initialState: { skippedFilter: [] } as ParamsBagStore,
  reducers: {
    setCurrentCompany: (state, { payload }: PayloadAction<Partial<Company>>) => {
      state.currentCompany = payload;
      sessionStorage.setItem('currentCompany', JSON.stringify(payload));
    },
    setAccountantBag: (state, { payload }: PayloadAction<Partial<Accountant>>) => {
      state.currentAccountant = payload;
      sessionStorage.setItem('currentAccountant', JSON.stringify(payload));
    },
    clearSecretBag: (state) => {
      state.secretBag = null;
    },
    setCurrentClient: (state, { payload }: PayloadAction<Partial<Client>>) => {
      state.currentClient = payload;
    },
    setCurrentBankAccount: (state, { payload }: PayloadAction<Partial<BankAccount>>) => {
      state.currentBankAccount = payload;
      sessionStorage.setItem('currentBankAccount', JSON.stringify(payload));
    },
    setSelectedBankAccounts: (state, { payload }: PayloadAction<Pick<BankAccount, 'accountNr' | 'originate'>[]>) => {
      state.selectedBankAccounts = payload;
      sessionStorage.setItem('selectedBankAccounts', JSON.stringify(payload));
    },
    clearTransactions: (state) => {
      state.transactionList = [];
    },
    removeFileNameFromLongPolling: (state, { payload }) => {
      state.longPolling = state.longPolling.filter((lp) => lp !== payload?.name);
    },
    resetLongPolling: (state) => {
      state.longPolling = [];
    },
    setCurrentFileId: (state, { payload }: PayloadAction<string>) => {
      state.currentFileId = payload;
    },
    setAccountList: (state, { payload }: PayloadAction<Aggreg8Account[]>) => {
      state.aggreg8AccountList = payload;
    },
    setBankList: (state, { payload }: PayloadAction<Aggreg8Bank[]>) => {
      state.aggreg8BankList = payload;
    },
    setTaxNumber: (state, { payload }: PayloadAction<string>) => {
      state.taxNumber = payload;
    },
    setAccountToCompanyListIsLoaded: (state, { payload }: PayloadAction<boolean>) => {
      state.accountToCompanyListIsLoaded = payload;
    },
    setTransactionViewerFilter: (state, { payload }: PayloadAction<TransactionFilter>) => {
      state.transactionViewerFilter = payload;
    },
    clearTransactionViewerFilter: (state) => {
      state.transactionViewerFilter = null;
    },
    setSkippedFilter: (state, { payload }) => {
      state.skippedFilter = payload;
    },
    setIsRlb: (state, { payload }) => {
      state.isRlb = payload;
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      clientApi.endpoints.createAccessToken.matchFulfilled,
      (state, { payload }: PayloadAction<{ client_id: string; client_secret: string }>) => {
        state.secretBag = { clientId: payload.client_id, clientSecret: payload.client_secret };
      }
    );
    builder.addMatcher(aggreg8Api.endpoints.postUserFlowInit.matchFulfilled, (state, { payload }) => {
      state.userFlowInitResponse = payload;
    });
    builder.addMatcher(
      bankAccountApi.endpoints.getBankAccountList.matchFulfilled,
      (state, { payload }: PayloadAction<BankAccount[]>) => {
        state.bankAccountList = payload;
        sessionStorage.setItem('bankAccountList', JSON.stringify(payload));
      }
    );
    builder.addMatcher(
      transactionApi.endpoints.getTransactionByBankAccount.matchFulfilled,
      (state, { payload }: PayloadAction<ExtendedTransaction[]>) => {
        state.transactionList = state.transactionList ? [...state.transactionList, ...payload] : [...payload];
        state.prevTransactions = payload;
      }
    );
    builder.addMatcher(fileApi.endpoints.fileUpload.matchFulfilled, (state, { payload }) => {
      state.longPolling = state?.longPolling ? [...state.longPolling, payload?.name] : [payload?.name];
    });
    builder.addMatcher(
      companyApi.endpoints.getCompanyList.matchFulfilled,
      (state, { payload }: PayloadAction<Company[]>) => {
        state.companyList = payload;
        state.options =
          payload && payload.length > 0
            ? payload.map((company) => ({
                label: company.name,
                value: company._id,
                existing: true,
              }))
            : ([] as OptionFromCompany[]);
      }
    );
    builder.addMatcher(
      aggreg8Api.endpoints.getAccounts.matchFulfilled,
      (state, { payload }: PayloadAction<Aggreg8Account[]>) => {
        state.aggreg8AccountList = payload;
        state.accountListLength = payload && payload?.length ? payload.length : 0;
      }
    );
    builder.addMatcher(
      aggreg8Api.endpoints.getBanks.matchFulfilled,
      (state, { payload }: PayloadAction<Aggreg8Bank[]>) => {
        state.aggreg8BankList = payload;
      }
    );
    builder.addMatcher(
      bankConfigApi.endpoints.getBankConfigs.matchFulfilled,
      (state, { payload }: PayloadAction<BankConfig[]>) => {
        state.bankConfigList = payload;
      }
    );
    builder.addMatcher(
      clientApi.endpoints.getAccessTokenList.matchFulfilled,
      (state, { payload }: PayloadAction<Client[]>) => {
        state.clientList = payload;
        sessionStorage.setItem('clientList', JSON.stringify(payload));
      }
    );
    builder.addMatcher(clientApi.endpoints.getAccessTokenList.matchRejected, (state) => {
      state.clientList = [];
    });
  },
});

export const {
  setCurrentCompany,
  setAccountantBag,
  clearSecretBag,
  setCurrentClient,
  setCurrentBankAccount,
  setSelectedBankAccounts,
  clearTransactions,
  resetLongPolling,
  removeFileNameFromLongPolling,
  setCurrentFileId,
  setTaxNumber,
  setAccountList,
  setBankList,
  setAccountToCompanyListIsLoaded,
  setTransactionViewerFilter,
  clearTransactionViewerFilter,
  setSkippedFilter,
  setIsRlb,
} = paramsBagSlice.actions;
export const paramsBagReducer = paramsBagSlice.reducer;

export const currentCompanySelector = createSelector(
  (state: RootState) => state.paramsBag.currentCompany,
  (currentCompany) => {
    return currentCompany ?? readFromSessionStorage('currentCompany');
  }
);

export const currentAccountantSelector = createSelector(
  (state: RootState) => state.paramsBag.currentAccountant,
  (currentAccountant) => {
    return currentAccountant ?? readFromSessionStorage('currentAccountant');
  }
);

export const currentBankAccountSelector = createSelector(
  (state: RootState) => state.paramsBag.currentBankAccount,
  (currentBankAccount) => {
    return currentBankAccount ?? readFromSessionStorage('currentBankAccount');
  }
);

export const selectedBankAccountsSelector = createSelector(
  (state: RootState) => state.paramsBag.selectedBankAccounts,
  (selectedBankAccounts) => {
    return selectedBankAccounts ?? readFromSessionStorage('selectedBankAccounts');
  }
);

export const currentClientSelector = createSelector(
  (state: RootState) => state.paramsBag.currentClient,
  (currentClient) => currentClient
);

export const longPollingSelector = createSelector(
  (state: RootState) => state.paramsBag.longPolling,
  (longPolling) => longPolling
);

export const transactionListSelector = createSelector(
  (state: RootState) => state.paramsBag.transactionList,
  (transactionList) => transactionList
);

export const prevTransactionSelector = createSelector(
  (state: RootState) => state.paramsBag.prevTransactions,
  (prevTransactions) => prevTransactions
);

export const bankAccountListSelector = createSelector(
  (state: RootState) => state.paramsBag.bankAccountList,
  (bankAccountList) => {
    return bankAccountList ?? readFromSessionStorage('bankAccountList');
  }
);

export const currentFileIdSelector = createSelector(
  (state: RootState) => state.paramsBag.currentFileId,
  (currentFileId) => currentFileId
);

export const secretBagSelector = createSelector(
  (state: RootState) => state.paramsBag.secretBag,
  (secretBag) => secretBag
);

export const userFlowInitResponseSelector = createSelector(
  (state: RootState) => state.paramsBag.userFlowInitResponse,
  (userFlowInitResponse) => userFlowInitResponse
);

export const taxNumberSelector = createSelector(
  (state: RootState) => state.paramsBag.taxNumber,
  (taxNumber) => taxNumber
);

export const aggreg8AccuntListSelector = createSelector(
  (state: RootState) => state.paramsBag.aggreg8AccountList,
  (accountList) => accountList
);

export const aggreg8BankListSelector = createSelector(
  (state: RootState) => state.paramsBag.aggreg8BankList,
  (bankList) => bankList
);

export const companyListSelector = createSelector(
  (state: RootState) => state.paramsBag.companyList,
  (companyList) => companyList
);

export const accountToCompanyListIsLoadedSelector = createSelector(
  (state: RootState) => state.paramsBag.accountToCompanyListIsLoaded,
  (accountToCompanyListIsLoaded) => accountToCompanyListIsLoaded
);

export const optionsFromCompanySelector = createSelector(
  (state: RootState) => state.paramsBag.options,
  (options) => options
);

export const accountListLengthSelector = createSelector(
  (state: RootState) => state.paramsBag.accountListLength,
  (accountListLength) => accountListLength
);

export const bankConfigListSelector = createSelector(
  (state: RootState) => state.paramsBag.bankConfigList,
  (bankConfigList) => bankConfigList
);

export const transactionViewerFilterSelector = createSelector(
  (state: RootState) => state.paramsBag.transactionViewerFilter,
  (transactionViewerFilter) => transactionViewerFilter
);

export const skippedFilterSelector = createSelector(
  (state: RootState) => state.paramsBag.skippedFilter,
  (skippedFilter) => skippedFilter
);

export const isRlbSelector = createSelector(
  (state: RootState) => state.paramsBag.isRlb,
  (isRlb) => isRlb
);

export const clientListSelector = createSelector(
  (state: RootState) => state.paramsBag.clientList,
  (clients) => clients ?? readFromSessionStorage('clientList')
);
